import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";

import Link from "@/components/wpl/link/src";
import { isLocalizableHref, useLocalizedHref } from "@/libraries/web-framework";
import {
    LogoV2 as Logo,
    LogoBrandV2 as LogoBrand,
    ProductKeyTypes
} from "@/libraries/wpl/foundations/src";

const containerCss = css({
    paddingBlock: "24px",
    display: "flex",
    alignItems: "center",
    gap: token("space.150")
});

const logoCss = css({
    padding: token("space.050"),
    borderRadius: "4px",
    background: "#F0F1F2",
    display: "flex",
    width: "44px",
    height: "44px",
    justifyContent: "center",
    alignItems: "center"
});

const textContainerCss = css({
    display: "flex",
    padding: token("space.0"),
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    flex: "1 0 0"
});

const nameCss = css({
    h3: {
        fontSize: "0.875rem",
        fontWeight: "700",
        lineHeight: "1.2"
    },
    color: "#101214",
    fontFamily: "Charlie Display",
    padding: token("space.0"),
    margin: "0",
    display: "flex"
});

const descriptionCss = css({
    color: "#505258",
    fontFamily: "Charlie Text",
    fontSize: "0.75rem",
    fontWeight: "400",
    margin: "0"
});

const basicLinkCss = css({
    fontFamily: "Charlie Text",
    fontSize: "16px",
    lineHeight: "20px",
    paddingBlock: "24px"
});

const pillContainerCss = css({
    color: "#09326C",
    backgroundColor: "#8FB8F6",
    display: "flex",
    alignItems: "center",
    paddingInline: "8px",
    borderRadius: "24px",
    marginInlineStart: "6px"
});

const pillTextCss = css({
    fontSize: "0.85rem",
    fontWeight: "700",
    textTransform: "uppercase"
});

const Pill = () => {
    const intl = useIntl();
    return (
        <div css={pillContainerCss}>
            <span css={pillTextCss}>
                {intl.formatMessage({
                    id: "globalnav.v2.pill",
                    defaultMessage: "New"
                })}
            </span>
        </div>
    );
};

export type MobileNavCardProps = {
    key: string;
    logo?: LogoBrand;
    href: string;
    name: string;
    description?: string;
    isNew?: boolean;
    productKey?: ProductKeyTypes;
};

export const MobileNavCard = ({
    logo,
    href,
    name,
    description,
    isNew,
    productKey = "home"
}: Omit<MobileNavCardProps, "key">) => {
    const localizedHref = useLocalizedHref(href);
    const formattedHref = isLocalizableHref(href) ? localizedHref : href;

    return (
        <>
            {description ? (
                <a
                    href={formattedHref}
                    css={containerCss}
                    aria-label={name}
                    target={isLocalizableHref(href) ? "" : "_blank"}
                    rel={isLocalizableHref(href) ? "" : "noreferrer"}
                    data-event="clicked"
                    data-event-component="linkButton"
                    data-event-container="navbarDropdown"
                    data-product-key={productKey}
                >
                    {logo && (
                        <div css={logoCss}>
                            <Logo
                                brand={logo}
                                type="mark"
                                appearance="brand"
                                width={32}
                                height={32}
                            />
                        </div>
                    )}
                    <div css={textContainerCss}>
                        <h5 css={nameCss}>
                            <span>{name}</span>
                            {isNew && <Pill />}
                        </h5>
                        <p css={descriptionCss}>{description}</p>
                    </div>
                </a>
            ) : (
                <Link
                    text={name}
                    href={formattedHref}
                    type="basic"
                    css={basicLinkCss}
                    ariaLabel={name}
                    dataEventProps={{
                        "data-product-key": productKey,
                        "data-event-component": "linkButton",
                        "data-event-container": "navbarDropdown",
                        "data-label": name,
                        "data-event": "clicked"
                    }}
                />
            )}
        </>
    );
};
